<template>
  <v-dialog :value="sectionEditor.show" :width="1200" @input="handleClosePopup">
    <div style="background-color: #fff">
      <div class="header-modal">
        <v-card-title class="d-flex justify-space-between align-center" style="padding: 10px 0">
          <span style="color: #0081c7">{{ fields.title }}</span>
          <v-btn icon @click="handleClosePopup">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
      </div>

      <v-card-text style="max-height: 70vh; overflow-y: auto">
        <div>
          <label class="label-input">Section Name</label>
          <v-text-field v-model="fields.title" label="Name" />
        </div>

        <div>
          <label class="label-input">Header Title</label>
          <ckeditor :editor="editor" v-model="fields.header" :config="editorConfig" />
        </div>

        <div class="mt-6">
          <label class="label-input mb-4">Compare Content</label>
          <div class="group-item">
            <v-checkbox v-model="fields.containerFullWidth" label="Container Full Width" dense />
            <v-text-field v-model="fields.vsBox.text" label="Compare Text" />
            <v-select v-model="fields.badPosition" :items="badPositionOptions" label="Bad Side Position" />
            <div>
              <div class="font-weight-medium pb-2">Images / Videos</div>
              <v-row>
                <v-col cols="6">
                  <div class="group-item pa-3">
                    <div class="font-weight-medium mb-2">Good Media</div>
                    <v-radio-group v-model="fields.leftMedia.type" row dense hide-details class="mt-0 mb-3">
                      <v-radio label="Image" value="image"></v-radio>
                      <v-radio label="Video" value="video"></v-radio>
                    </v-radio-group>
                    <div v-if="fields.leftMedia.type === 'image'">
                      <UploadImage
                        ref="leftImageUpload"
                        @callback="handleUploadLeftImage"
                        :image="{ imageSrc: fields.leftMedia.url }"
                        :height="`200`"
                        :typeUpload="'image'"
                        @callbackDelete="handleRemoveLeftImage"
                      />
                    </div>
                    <div v-else>
                      <div class="d-flex align-center gap-4 mb-3">
                        <v-text-field v-model="fields.leftMedia.video" label="Video URL" dense hide-details />
                        <v-btn @click="handlePreviewVideo('leftVideoPlayer')" text color="primary" small>
                          <v-icon small>mdi-reload</v-icon>
                        </v-btn>
                      </div>
                      <div class="video-wrapper" v-if="fields.leftMedia.video">
                        <div class="wrap-video">
                          <video width="100%" height="auto" autoplay muted loop playsinline ref="leftVideoPlayer">
                            <source type="video/mp4" :src="fields.leftMedia.video" />
                          </video>
                        </div>
                      </div>
                    </div>
                  </div>
                </v-col>
                <v-col cols="6">
                  <div class="group-item pa-3">
                    <div class="font-weight-medium mb-2">Bad Media</div>
                    <v-radio-group v-model="fields.rightMedia.type" row dense hide-details class="mt-0 mb-3">
                      <v-radio label="Image" value="image"></v-radio>
                      <v-radio label="Video" value="video"></v-radio>
                    </v-radio-group>
                    <div v-if="fields.rightMedia.type === 'image'">
                      <UploadImage
                        ref="rightImageUpload"
                        @callback="handleUploadRightImage"
                        :image="{ imageSrc: fields.rightMedia.url }"
                        :height="`200`"
                        :typeUpload="'image'"
                        @callbackDelete="handleRemoveRightImage"
                      />
                    </div>
                    <div v-else>
                      <div class="d-flex align-center gap-4 mb-3">
                        <v-text-field v-model="fields.rightMedia.video" label="Video URL" dense hide-details />
                        <v-btn @click="handlePreviewVideo('rightVideoPlayer')" text color="primary" small>
                          <v-icon small>mdi-reload</v-icon>
                        </v-btn>
                      </div>
                      <div class="video-wrapper" v-if="fields.rightMedia.video">
                        <div class="wrap-video">
                          <video width="100%" height="auto" autoplay muted loop playsinline ref="rightVideoPlayer">
                            <source type="video/mp4" :src="fields.rightMedia.video" />
                          </video>
                        </div>
                      </div>
                    </div>
                  </div>
                </v-col>
              </v-row>
            </div>
            <div class="mt-6">
              <div class="d-flex justify-space-between align-center mb-3">
                <div class="font-weight-medium">Compare List ({{ fields.compareList.length }})</div>
                <v-btn small color="primary" @click="handleAddCompareItem">
                  <v-icon size="16" left>mdi-plus</v-icon>
                  Add Item
                </v-btn>
              </div>
              <div v-if="fields.compareList.length === 0" class="text-center pa-4 grey--text group-item">
                No items added yet. Click "Add Item" to get started.
              </div>
              <v-expansion-panels v-else accordion>
                <v-expansion-panel v-for="(item, i) in fields.compareList" :key="i">
                  <v-expansion-panel-header>
                    <div class="d-flex justify-space-between align-center" style="width: 100%">
                      <div class="d-flex align-center" style="gap: 4px">
                        <v-chip small color="green" text-color="white">{{ item.goodText || 'Good' }}</v-chip>
                        <span class="grey--text">vs</span>
                        <v-chip small color="grey lighten-2">{{ item.badText || 'Bad' }}</v-chip>
                      </div>
                      <v-btn icon small @click.stop="handleRemoveCompareItem(i)">
                        <v-icon size="20" color="#ff6c6c">mdi-delete</v-icon>
                      </v-btn>
                    </div>
                  </v-expansion-panel-header>
                  <v-expansion-panel-content>
                    <div class="pa-2">
                      <v-row>
                        <v-col cols="6">
                          <v-text-field
                            v-model="item.goodText"
                            label="Good Text"
                            dense
                            hide-details
                            prepend-icon="mdi-check-circle"
                          />
                        </v-col>
                        <v-col cols="6">
                          <v-text-field
                            v-model="item.badText"
                            label="Bad Text"
                            dense
                            hide-details
                            prepend-icon="mdi-close-circle"
                          />
                        </v-col>
                      </v-row>
                    </div>
                  </v-expansion-panel-content>
                </v-expansion-panel>
              </v-expansion-panels>
            </div>
          </div>
        </div>

        <div class="mt-6">
          <label class="label-input">Section Settings</label>
          <div class="group-item">
            <div>
              <div class="font-weight-medium">Spacing</div>
              <v-row>
                <v-col cols="6">
                  <v-checkbox v-model="fields.topPadding" label="Top Spacing" dense hide-details />
                </v-col>
                <v-col cols="6">
                  <v-checkbox v-model="fields.bottomPadding" label="Bottom Spacing" dense hide-details />
                </v-col>
              </v-row>
            </div>
            <div class="mt-6">
              <div class="font-weight-medium mb-3">Container Colors</div>
              <v-row>
                <v-col md="6" cols="12">
                  <ColorPicker
                    v-model="fields.colors.backgroundColor"
                    name="backgroundColor"
                    label="Background Color"
                    default-color="#f7eee7"
                  />
                </v-col>
                <v-col md="6" cols="12">
                  <ColorPicker
                    v-model="fields.colors.textColor"
                    name="textColor"
                    label="Text Color"
                    default-color="#000000"
                  />
                </v-col>
              </v-row>
            </div>
            <div class="mt-6">
              <div class="font-weight-medium mb-3">Good Item Colors</div>
              <v-row>
                <v-col md="6" cols="12">
                  <ColorPicker
                    v-model="fields.colors.goodBackgroundColor"
                    name="goodBackgroundColor"
                    label="Background Color"
                    default-color="#4dc61c"
                  />
                </v-col>
                <v-col md="6" cols="12">
                  <ColorPicker
                    v-model="fields.colors.goodTextColor"
                    name="goodTextColor"
                    label="Text Color"
                    default-color="#ffffff"
                  />
                </v-col>
              </v-row>
            </div>
            <div class="mt-6">
              <div class="font-weight-medium mb-3">Bad Item Colors</div>
              <v-row>
                <v-col md="6" cols="12">
                  <ColorPicker
                    v-model="fields.colors.badBackgroundColor"
                    name="badBackgroundColor"
                    label="Background Color"
                    default-color="#f3f3f3"
                  />
                </v-col>
                <v-col md="6" cols="12">
                  <ColorPicker
                    v-model="fields.colors.badTextColor"
                    name="badTextColor"
                    label="Text Color"
                    default-color="#000000"
                  />
                </v-col>
              </v-row>
            </div>
            <div class="mt-6">
              <div class="font-weight-medium mb-3">Compare Box Colors</div>
              <v-row>
                <v-col cols="6">
                  <ColorPicker
                    v-model="fields.vsBox.backgroundColor"
                    name="vsBackgroundColor"
                    label="Background Color"
                    default-color="#121212"
                  />
                </v-col>
                <v-col cols="6">
                  <ColorPicker
                    v-model="fields.vsBox.textColor"
                    name="vsTextColor"
                    label="Text Color"
                    default-color="#ffffff"
                  />
                </v-col>
              </v-row>
            </div>
          </div>
        </div>
      </v-card-text>

      <div class="footer-modal">
        <v-card-actions style="padding: 10px 0">
          <v-spacer></v-spacer>
          <v-btn min-width="100" depressed @click="handleClosePopup">Cancel</v-btn>
          <v-btn min-width="100" color="primary" @click="handleSave">Save Changes</v-btn>
        </v-card-actions>
      </div>
    </div>
  </v-dialog>
</template>

<script>
import { cloneDeepObject } from '../common/utils';
import { updateHtmlFromData } from '../common/html/update';
import UploadImage from '@/components/form/uploadFile/UpdateImage.vue';
import ColorPicker from './common/color-picker.vue';
import Editor from 'ckeditor5-custom-build/build/ckeditor';
import { ckeditorConfigFullToolbar } from '@/helpers/editor';

export default {
  name: 'card-compare-product',
  components: {
    UploadImage,
    ColorPicker,
  },
  props: {
    sectionEditor: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      editor: Editor,
      editorConfig: ckeditorConfigFullToolbar,
      badPositionOptions: [
        { text: 'Right', value: 'right' },
        { text: 'Left', value: 'left' },
      ],
      fields: {
        title: 'Card Compare Product',
        header: '',
        leftMedia: {
          url: '',
          video: '',
          type: 'image',
        },
        rightMedia: {
          url: '',
          video: '',
          type: 'image',
        },
        vsBox: {
          text: 'VS.',
          backgroundColor: '#121212',
          textColor: '#ffffff',
        },
        badPosition: 'right',
        compareList: [],
        colors: {
          backgroundColor: '#f7eee7',
          textColor: '#000000',
          goodBackgroundColor: '#4dc61c',
          goodTextColor: '#ffffff',
          badBackgroundColor: '#f3f3f3',
          badTextColor: '#000000',
        },
        containerFullWidth: false,
        topPadding: true,
        bottomPadding: true,
      },
    };
  },
  methods: {
    normalizeFieldsData(data) {
      const nextData = cloneDeepObject(data || {});

      if (!nextData.leftMedia) {
        nextData.leftMedia = { url: '', video: '', type: 'image' };
      }
      if (!nextData.leftMedia.type) {
        nextData.leftMedia.type = nextData.leftMedia.video ? 'video' : 'image';
      }
      if (!nextData.rightMedia) {
        nextData.rightMedia = { url: '', video: '', type: 'image' };
      }
      if (!nextData.rightMedia.type) {
        nextData.rightMedia.type = nextData.rightMedia.video ? 'video' : 'image';
      }
      if (!nextData.vsBox) {
        nextData.vsBox = { text: 'VS.', backgroundColor: '#121212', textColor: '#ffffff' };
      }
      if (!nextData.compareList) {
        nextData.compareList = [];
      }
      if (!nextData.colors) {
        nextData.colors = {
          backgroundColor: '#f7eee7',
          textColor: '#000000',
          goodBackgroundColor: '#4dc61c',
          goodTextColor: '#ffffff',
          badBackgroundColor: '#f3f3f3',
          badTextColor: '#000000',
        };
      }
      if (!nextData.badPosition) {
        nextData.badPosition = 'right';
      }

      return nextData;
    },

    handleClosePopup() {
      this.$emit('close');
    },

    handleSave() {
      const updatedSection = {
        ...this.sectionEditor.section,
        title: this.fields.title,
        html: updateHtmlFromData.sectionCardCompareProduct(this.sectionEditor.section.html, this.fields),
      };
      this.$emit('update:section', updatedSection);
      this.handleClosePopup();
    },

    handleUploadLeftImage(obj) {
      if (obj && obj.imageSrc) {
        this.fields.leftMedia.url = obj.imageSrc;
      }
    },
    handleRemoveLeftImage() {
      this.fields.leftMedia.url = '';
    },

    handleUploadRightImage(obj) {
      if (obj && obj.imageSrc) {
        this.fields.rightMedia.url = obj.imageSrc;
      }
    },
    handleRemoveRightImage() {
      this.fields.rightMedia.url = '';
    },

    handlePreviewVideo(refName) {
      const video = this.$refs[refName];
      if (video) {
        video.load();
        video.play();
      }
    },

    handleAddCompareItem() {
      this.fields.compareList.push({
        goodText: 'Good Feature',
        badText: 'Bad Feature',
      });
    },

    handleRemoveCompareItem(index) {
      this.fields.compareList.splice(index, 1);
    },
  },

  mounted() {
    if (this.sectionEditor.sectionExtract) {
      this.fields = this.normalizeFieldsData(this.sectionEditor.sectionExtract);
    }
  },

  watch: {
    sectionEditor: {
      handler(newVal) {
        if (newVal.sectionExtract) {
          this.fields = this.normalizeFieldsData(newVal.sectionExtract);
        }
      },
      deep: true,
    },
  },
};
</script>

<style scoped lang="scss">
::v-deep .ck-editor__editable_inline {
  min-height: 100px !important;
}
::v-deep .ck-editor__editable {
  min-height: 100px !important;
}

::v-deep .v-text-field--outlined {
  .v-input__control {
    .v-input__slot {
      min-height: 40px;
    }
  }
}

.video-wrapper {
  background: rgba(0, 0, 0, 0.767);
  border-radius: 8px;
  .wrap-video {
    max-width: 186px;
    margin: 0 auto;
    video {
      display: block;
      width: 100%;
      height: auto;
    }
  }
}
</style>
